import React from 'react';
import DraggableResizableButton from './DraggableResizableButton';
import { ImageElementType } from '../../../redux/TabTile/TabStateInterfaces';
import { useSelector, useDispatch } from 'react-redux';
import { ColorSchemes } from '../../ChartOptions/Color/ColorScheme';
import {
  updateImageElement,
  removeImageElement,
  setSelectedImageId,
  setSelectedRichTextId,
} from '../../../redux/TabTile/TabActions';
import { RootState } from '../../../redux';
import { setFiltersOfGroup } from '../../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import { getAllFilterOptions } from '../../ChartFilterGroup/groups/getAllFilterOptions';
import { callQueryAPI } from '../../../redux/ChartPoperties/ChartControlsActions';
import { callQueryAPIDashboard } from '../../../redux/TabTile/TabTileActionsAndMultipleDispatches';

interface ButtonProps {
  currentTabId: number;
  backgroundColor: string;
  dashMode: string;
  softUI?: boolean;
  colorScheme?: string;
  sidebarOpen?: boolean;
  setSidebarOpen?: (val: boolean) => void;
  showObjectsSidebar?: boolean;
  setShowListofTileMenu?: (val: boolean) => void;
  setDashboardResizeColumn?: (val: boolean) => void;
  setLayerMode?: React.Dispatch<React.SetStateAction<'tiles' | 'objects'>>;
  setShowObjectsSidebar?: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedObject?: (object: string) => void;
  setShowDashboardFilterEdit?: (val: boolean) => void;
  selectTab?: (tabName: string, tabId: number, isFromNavigation: boolean, dashMode: string) => void;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  selectTile?: (
    tabId: number,
    tileName: string,
    tileId: number,
    nextTileId: number,
    fromTab: boolean,
  ) => void;
  tabState: any;
  dashboardHeight?: number;
  dashboardWidth?: number;
}

const Button: React.FC<ButtonProps> = ({
  currentTabId,
  dashMode,
  colorScheme = 'default',
  backgroundColor,
  setSidebarOpen,
  softUI,
  showObjectsSidebar,
  setShowListofTileMenu,
  setDashboardResizeColumn,
  setShowObjectsSidebar,
  setLayerMode,
  setSelectedObject,
  setShowDashboardFilterEdit,
  selectTab,
  selectTile,
  dashboardHeight,
  dashboardWidth,
  tabState,
  setLoading = () => {},
}) => {
  const dispatch = useDispatch();

  const { tab, gridSize, schemeBackground, chartGroup, token, tabTileProps, dashBoardFilterGroup } =
    useSelector((state: RootState) => {
      const currentTab = state.tabState.tabs[currentTabId];
      const schemeName = currentTab?.dashboardState?.colorScheme || 'peacock';
      const scheme = ColorSchemes.find((s) => s.name === schemeName);

      return {
        tab: currentTab,
        gridSize: state.tabTileProps.dashGridSize,
        schemeBackground: scheme?.background || '#ffffff',
        chartGroup: state.chartFilterGroup,
        dashBoardFilterGroup: state.dashBoardFilterGroup,
        token: state.isLogged.accessToken,
        tabTileProps: state.tabTileProps,
      };
    });

  const { imageElements, selectedImageId } = tab.dashboardObjects;

  const handleButtonClick = async (
    event: React.MouseEvent,
    el: ImageElementType,
    selectedImageId: string | null,
  ) => {
    const isPresentationMode = dashMode === 'Present' || dashMode === 'TV Display'; //
    const isModifierPressed = event.ctrlKey || event.metaKey;
    const shouldTriggerAction = isPresentationMode ? !isModifierPressed : isModifierPressed;

    if (el.actionType === 'weburl' && el.actionValue && shouldTriggerAction) {
      let url = el.actionValue;

      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`;
      }
      window.open(url, '_blank');
    } else if (el.actionType === 'tabnavigation' && el.actionValue && shouldTriggerAction) {
      const tabId = Number(el.actionValue);
      const tab = tabState.tabs[tabId];
      if (tab) {
        selectTab?.(tab.tabName, tabId, true, dashMode);
        selectTile?.(tabId, tab.selectedTileName, tab.selectedTileId, tab.nextTileId, true);

        setSidebarOpen?.(true);
        setShowObjectsSidebar?.(false);
        setShowListofTileMenu?.(true);
      }
    } else if (el.actionType === 'resetfiltergroups' && shouldTriggerAction) {
      const groupIds = el.actionValues?.length
        ? el.actionValues
        : el.actionValue
          ? [el.actionValue]
          : [];

      const propKeysForAPICall: Set<string> = new Set();

      for (const groupId of groupIds) {
        const rawGroup = chartGroup.groups[groupId];
        if (!rawGroup) return;
        const groupObj = {
          ...rawGroup,
          id: groupId,
          filters: JSON.parse(JSON.stringify(rawGroup.filters || [])),
        };
        const dsDetails = tabTileProps.selectedDataSetList.find(
          (dataset: any) => String(dataset.id) === String(rawGroup.dataSetId),
        );
        if (dsDetails) {
          await getAllFilterOptions({
            group: groupObj,
            dsDetails,
            token,
            dispatch,
            chartGroup,
            dashBoardFilterGroup,
            setLoading,
            fromButtonAction: true,
          });
        }
        Object.entries(chartGroup.tabTile).forEach(([tabTileId, arr]: any) => {
          if (dashBoardFilterGroup.filterGroupTabTiles[groupObj.id]?.includes(tabTileId)) {
            propKeysForAPICall.add(tabTileId);
          }
          if (arr.includes(groupObj.id)) {
            propKeysForAPICall.add(tabTileId);
          }
        });
      }
      propKeysForAPICall.forEach((tabTileId) => {
        dispatch(callQueryAPI(tabTileId, true));
      });
      if (propKeysForAPICall.size > 0) {
        dispatch(callQueryAPIDashboard(true));
      }
    } else if (!isPresentationMode) {
      // In edit mode, select the button and open the sidebar
      dispatch(setSelectedRichTextId(currentTabId, null));
      dispatch(setSelectedImageId(currentTabId, el.id));
      setSidebarOpen?.(true);
      setShowObjectsSidebar?.(false);
      setShowListofTileMenu?.(true);
      setLayerMode?.('objects');
      setSelectedObject?.('button');
      setDashboardResizeColumn?.(false);
      setShowDashboardFilterEdit?.(false);
    }
  };
  const handleButtonDelete = (id: string) => {
    dispatch(removeImageElement(currentTabId, id));
    if (selectedImageId === id) {
      dispatch(setSelectedImageId(currentTabId, null));
    }
  };

  const handleButtonChange = (id: string, updates: Partial<ImageElementType>) => {
    dispatch(updateImageElement(currentTabId, id, updates));
  };

  return (
    <>
      {imageElements
        .filter((el: ImageElementType) => el.type === 'icon-button')
        .map((el: ImageElementType) => {
          const hasUrl = el.actionType === 'weburl' && el.actionValue;

          return (
            <DraggableResizableButton
              key={el.id}
              id={el.id}
              x={el.x}
              y={el.y}
              width={el.width}
              height={el.height}
              backgroundColor={el.backgroundColor ?? schemeBackground}
              backgroundAlpha={el.backgroundAlpha ?? 100}
              borderColor={el.borderColor}
              iconColor={el.iconColor ?? ''}
              hasUrl={hasUrl}
              hoverEnabled={el.hoverEnabled ?? true}
              tooltip={el.tooltip}
              iconScale={el.iconScale ?? 1}
              dashMode={dashMode}
              borderWidth={el.borderWidth}
              borderRadius={el.borderRadius}
              selected={selectedImageId === el.id}
              icon={el.icon}
              iconStyle={
                typeof el.iconStyle === 'object' && el.iconStyle !== null
                  ? el.iconStyle
                  : { default: el.iconStyle || 'outlined', hover: el.iconStyle || 'outlined' }
              }
              onDelete={handleButtonDelete}
              onClick={(event: React.MouseEvent) => handleButtonClick(event, el, selectedImageId)}
              onChange={(updates: Partial<ImageElementType>) => handleButtonChange(el.id, updates)}
              contentOrder={
                typeof el.contentOrder === 'object' && !Array.isArray(el.contentOrder)
                  ? el.contentOrder
                  : { default: el.contentOrder || ['icon'] }
              }
              text={el.text ?? ''}
              softUI={softUI}
              colorScheme={colorScheme}
              imageUrl={el.imageUrl ?? ''}
              textProps={el.textProps ?? {}}
              iconProps={el.iconProps ?? {}}
              imageProps={el.imageProps ?? {}}
              containerProps={el.containerProps ?? {}}
              dashboardHeight={dashboardHeight}
              dashboardWidth={dashboardWidth}
              gridSize={gridSize}
            />
          );
        })}
    </>
  );
};

export default Button;
